import type React from 'react';

/**
 * Shared table presentation, matching the Projects list: a hairline bordered
 * container, a muted header strip, subtle alternating rows and a row divider.
 */
export const TABLE_CONTAINER_CLASS =
  'rounded-lg border border-border overflow-hidden bg-card shrink-0';

/** Header row of a table — muted strip that does not react to hover. */
export const TABLE_HEADER_ROW_CLASS = 'bg-muted/30 hover:bg-muted/30 border-b border-border';

/** Header cell text treatment. */
export const TABLE_HEAD_CLASS = 'text-xs font-medium text-muted-foreground';

/** Data row: divider, striping and hover feedback via utility classes. */
export const TABLE_ROW_CLASS = 'border-b border-border transition-colors even:bg-muted/30 hover:bg-muted/40';

/** Data cell padding. */
export const TABLE_CELL_CLASS = 'py-3';

/** Selected row: keeps its highlight through striping and hover. */
export const TABLE_ROW_SELECTED_CLASS = 'bg-primary/10 even:bg-primary/10 hover:bg-primary/15';

/**
 * Table container that scrolls its own rows, for dialogs where the header
 * stays put and the pagination bar sits underneath.
 */
export const TABLE_SCROLL_CONTAINER_CLASS =
  'rounded-lg border border-border bg-card overflow-hidden min-h-0 flex flex-col ' +
  // The `Table` primitive owns the scrollport, so the sticky header only pins if
  // that element — not this one — is what scrolls.
  '[&>[data-slot=table-container]]:flex-1 [&>[data-slot=table-container]]:overflow-auto';

/**
 * Sticky header cells scroll over the rows, so they need an opaque fill —
 * the flat equivalent of the translucent `bg-muted/30` header strip.
 */
export const TABLE_STICKY_HEADER_BG = 'color-mix(in oklab, var(--muted) 30%, var(--card))';

export const TABLE_STICKY_HEADER_STYLE: React.CSSProperties = {
  background: TABLE_STICKY_HEADER_BG,
};

/**
 * Raw `<table>` equivalent used by the report screens, adding the row
 * dividers that the `Table` primitive applies for the list screens.
 */
export const RAW_TABLE_CLASS =
  'w-full border-collapse text-[13px] [&_tbody>tr]:border-b [&_tbody>tr]:border-border';

/**
 * Container for the raw report tables. Report screens sit inside a scrolling
 * flex column, so the container must keep its natural height (`shrink-0`) and
 * must not clip vertically — the rounded corners are clipped by the inner
 * horizontal scroller instead.
 */
export const RAW_TABLE_CONTAINER_CLASS = 'rounded-lg border border-border bg-card shrink-0';

/** Horizontal scroller wrapping a raw report table. */
export const RAW_TABLE_SCROLL_CLASS = 'overflow-x-auto rounded-lg';

/**
 * The report screens render raw `<table>` markup and set row backgrounds
 * inline, so the striping and hover colours are also exposed as CSS values.
 * These mirror `bg-muted/30` and `bg-muted/40` used by the class variants.
 */
export const TABLE_STRIPE_BG = 'color-mix(in oklab, var(--muted) 30%, transparent)';
export const TABLE_HOVER_BG = 'color-mix(in oklab, var(--muted) 40%, transparent)';

/** Alternating row background matching the Projects list. */
export function alternatingRowBackground(index: number): string {
  return index % 2 === 0 ? 'var(--background)' : TABLE_STRIPE_BG;
}

export function alternatingRowStyle(index: number): React.CSSProperties {
  return { background: alternatingRowBackground(index) };
}

/**
 * Hover feedback for alternating rows — inline backgrounds win over CSS
 * hover rules, so the row restores its own stripe colour on leave.
 */
export function alternatingRowHoverHandlers(index: number) {
  return {
    onMouseEnter: (e: React.MouseEvent<HTMLTableRowElement>) => {
      e.currentTarget.style.background = TABLE_HOVER_BG;
    },
    onMouseLeave: (e: React.MouseEvent<HTMLTableRowElement>) => {
      e.currentTarget.style.background = alternatingRowBackground(index);
    },
  };
}
